import React, { useState } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import {
  Settings,
  LocationOn,
  Face,
  Chat,
  DirectionsCar,
  Analytics,
} from "@mui/icons-material";

// Animation keyframes
const slideInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(100px);
  }
  70% {
    opacity: 1;
    transform: translateY(-10px);
  } 
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromRight = keyframes`
  0% {
    opacity: 0;
    transform: translateX(100px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const floatAnimation = keyframes`
  0%, 100% { 
    transform: translateY(0px) rotate(0deg); 
  }
  50% { 
    transform: translateY(-15px) rotate(5deg); 
  }
`;

const pulseGlow = keyframes`
  0%, 100% { 
    box-shadow: 0 8px 25px rgba(45, 134, 89, 0.3);
  }
  50% { 
    box-shadow: 0 12px 35px rgba(45, 134, 89, 0.6);
  }
`;

const gradientShift = keyframes`
  0%, 100% { 
    background-position: 0% 50%;
  }
  50% { 
    background-position: 100% 50%;
  }
`;

// Styled Components
const HeaderContainer = styled(Box)({
  textAlign: "center",
  marginBottom: "10px",
  padding: "12px",
  background:
    "linear-gradient(135deg, #2d8659 0%, #080808ff 50%, #2d8659 100%)",
  backgroundSize: "200% 200%",
  animation: `${gradientShift} 6s ease infinite, ${slideInUp} 0.8s ease-out`,
  borderRadius: "20px",
  color: "white",
  boxShadow: "0 10px 30px rgba(45, 134, 89, 0.4)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)",
    animation: `${slideInFromRight} 2s ease-in-out infinite`,
  },
});

const MainTitle = styled(Typography)({
  fontSize: "2rem",
  fontWeight: 800,
  marginBottom: "4px",
  textShadow: "2px 2px 8px rgba(0,0,0,0.3)",
  position: "relative",
  zIndex: 2,
  "@media (max-width: 768px)": {
    fontSize: "3rem",
  },
});

const Subtitle = styled(Typography)({
  fontSize: "1.6rem",
  fontWeight: 500,
  opacity: 0.95,
  position: "relative",
  zIndex: 2,
});

const FeaturesGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
  gap: "10px",
  overflow: "auto",
  padding: "10px",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "rgba(45, 134, 89, 0.1)",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "linear-gradient(135deg, #2d8659 0%, #4caf50 100%)",
    borderRadius: "10px",
  },
});

const FeatureCard = styled(Card)(({ index, hovered }) => ({
  border: `2px solid ${hovered ? "#4caf50" : "#e0e0e0"}`,
  backgroundColor: hovered ? "#f1f8e9" : "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${slideInUp} 0.8s ease-out ${index * 0.15}s both`,
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  borderRadius: "16px",
  boxShadow: hovered
    ? "0 20px 40px rgba(76, 175, 80, 0.3)"
    : "0 8px 25px rgba(0, 0, 0, 0.1)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #2d8659, #4caf50, #2d8659)",
    transform: hovered ? "scaleX(1)" : "scaleX(0)",
    transformOrigin: "left",
    transition: "transform 0.4s ease",
  },

  "&:hover": {
    transform: "translateY(-8px) scale(1.02)",
    borderColor: "#4caf50",
  },
}));

const IconContainer = styled(Box)(({ hovered }) => ({
  color: "white",
  background: "linear-gradient(135deg, #2d8659 0%, #000000ff 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  borderRadius: "15px",
  flexShrink: 0,
  fontSize: "1.8rem",
  boxShadow: "0 6px 20px rgba(45, 134, 89, 0.4)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  transform: hovered ? "scale(1.15) rotate(10deg)" : "scale(1) rotate(0deg)",
  animation: `${floatAnimation} 3s ease-in-out infinite`,
  "&:hover": {
    animation: `${pulseGlow} 1s ease-in-out infinite`,
  },
}));

const StatsBadge = styled(Box)(({ hovered }) => ({
  display: "inline-flex",
  alignItems: "center",
  background: "linear-gradient(135deg, #2d8659 0%, #000000ff 100%)",
  color: "white",
  padding: "8px 16px",
  borderRadius: "20px",
  fontWeight: 700,
  fontSize: "1.2rem",
  transition: "all 0.3s ease",
  transform: hovered ? "scale(1.1)" : "scale(1)",
  boxShadow: hovered
    ? "0 6px 20px rgba(45, 134, 89, 0.4)"
    : "0 4px 15px rgba(45, 134, 89, 0.3)",
}));

const FloatingParticle = styled(Box)(({ index }) => ({
  position: "absolute",
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  background: "linear-gradient(135deg, #2d8659 0%, #4caf50 100%)",
  animation: `${floatAnimation} 3s ease-in-out infinite`,
  animationDelay: `${index * 0.5}s`,
  opacity: 0.6,
}));

const KeyFeatures = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const features = [
    {
      id: 1,
      title: "Unified Governance",
      description:
        "Decentralized operations for managing 50,000+ TPD Municipal Solid Waste Collection & Disposal across Punjab with integrated management systems.",
      icon: Settings,
      stats: "50,000+ TPD",
      particles: [
        { top: "10%", left: "5%" },
        { top: "80%", left: "8%" },
        { top: "20%", right: "5%" },
        { top: "70%", right: "8%" },
      ],
    },
    {
      id: 2,
      title: "Geotagged Infrastructure",
      description:
        "Comprehensive mapping of 6,600+ waste enclosures, transfer stations and 140 landfill sites with real-time monitoring capabilities.",
      icon: LocationOn,
      stats: "6,600+ Sites",
      particles: [
        { top: "15%", left: "7%" },
        { top: "85%", left: "5%" },
        { top: "25%", right: "7%" },
      ],
    },
    {
      id: 3,
      title: "AI-Powered Workforce Management",
      description:
        "Geo-fenced facial recognition attendance system monitoring 150,000+ sanitation workers with automated payroll and performance tracking.",
      icon: Face,
      stats: "150,000+ Workers",
      particles: [
        { top: "12%", left: "6%" },
        { top: "75%", left: "4%" },
        { top: "35%", right: "6%" },
        { top: "65%", right: "4%" },
      ],
    },
    {
      id: 4,
      title: "Citizen Engagement Platform",
      description:
        "Robust feedback model (1139) enabling real-time citizen complaints, suggestions, and service requests with automated resolution tracking.",
      icon: Chat,
      stats: "1139 Helpline",
      particles: [
        { top: "18%", left: "8%" },
        { top: "82%", right: "6%" },
        { top: "45%", left: "3%" },
      ],
    },
    {
      id: 5,
      title: "Smart Fleet Management",
      description:
        "Vehicle Tracking Management System (VTMS) monitoring 30,000+ specialized sanitation vehicles with optimized routes and fuel management.",
      icon: DirectionsCar,
      stats: "30,000+ Vehicles",
      particles: [
        { top: "8%", right: "8%" },
        { top: "88%", right: "3%" },
        { top: "28%", left: "8%" },
        { top: "68%", left: "3%" },
      ],
    },
    {
      id: 6,
      title: "Performance Analytics",
      description:
        "Real-time Performance Evaluation Dashboard (PED) with AI-driven KPI monitoring, predictive analytics, and automated reporting systems.",
      icon: Analytics,
      stats: "Live KPI Dashboard",
      particles: [
        { top: "12%", left: "4%" },
        { top: "78%", left: "6%" },
        { top: "32%", right: "4%" },
        { top: "72%", right: "6%" },
      ],
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        background:
          "linear-gradient(135deg, #f8fdf8 0%, #e8f5e9 50%, #f8fdf8 100%)",
        backgroundSize: "200% 200%",
        animation: `${gradientShift} 10s ease infinite`,
        position: "relative",
        overflow: "hidden",
        padding: "24px",
        borderRadius: "50px",
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-50px",
          right: "-50px",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          background:
            "linear-gradient(135deg, rgba(45, 134, 89, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)",
          animation: `${floatAnimation} 6s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-30px",
          left: "-30px",
          width: "150px",
          height: "150px",
          borderRadius: "50%",
          background:
            "linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(45, 134, 89, 0.05) 100%)",
          animation: `${floatAnimation} 8s ease-in-out infinite reverse`,
        }}
      />

      <HeaderContainer>
        <MainTitle>🔑 Key Features</MainTitle>
        <Subtitle>
          6 Advanced Features Powering Punjab's Waste Management Revolution
        </Subtitle>
      </HeaderContainer>

      <FeaturesGrid>
        {features.map((feature, index) => {
          const Icon = feature.icon;
          const isHovered = hoveredIndex === index;

          return (
            <FeatureCard
              key={feature.id}
              index={index}
              hovered={isHovered}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Floating particles */}
              {feature.particles.map((particle, particleIndex) => (
                <FloatingParticle
                  key={particleIndex}
                  index={particleIndex}
                  sx={{
                    top: particle.top,
                    left: particle.left,
                    right: particle.right,
                  }}
                />
              ))}

              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 3,
                  padding: "20px",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <IconContainer hovered={isHovered}>
                  <Icon fontSize="inherit" />
                </IconContainer>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 1.5,
                      fontSize: "1.5rem",
                      lineHeight: 1,
                      transition: "all 0.3s ease",
                      background:
                        "linear-gradient(135deg, #2d8659 0%, #000000ff 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      transform: isHovered
                        ? "translateX(5px)"
                        : "translateX(0)",
                    }}
                  >
                    {feature.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.2,
                      fontSize: "1.1rem",
                      mb: 2,
                      transition: "all 0.3s ease",
                      color: isHovered ? "#333" : "#555",
                      transform: isHovered
                        ? "translateX(3px)"
                        : "translateX(0)",
                    }}
                  >
                    {feature.description}
                  </Typography>

                  <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                    <StatsBadge hovered={isHovered}>{feature.stats}</StatsBadge>
                  </Box>
                </Box>
              </CardContent>
            </FeatureCard>
          );
        })}
      </FeaturesGrid>
    </Box>
  );
};

export default KeyFeatures;
